import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { estimatesApi, shiftLogApi } from '../services/api';
import { useEstimates, estimatesQueryKey } from '../hooks/useEstimates';
import useAuthStore from '../store/authStore';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import toast from 'react-hot-toast';
import type { GenerationEstimate, DailyGenerationSummary } from '../types';
import {
  BS_MONTHS_FULL, BS_MONTHS_SHORT,
  adStrToBS, bsToAD, bsDaysInMonth, bsYearRangeAD,
  getCurrentBSYear, getCurrentBSMonth, getBSYearOptions,
} from '../utils/bs';

const MONTHS = BS_MONTHS_SHORT;
const CURRENT_YEAR = getCurrentBSYear();
const BS_YEAR_OPTIONS = getBSYearOptions(10);

type PlanTab = 'year' | 'month';

// ── Helpers ───────────────────────────────────────────────────────────────────

function isoWeek(d: Date): number {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

function fmtKwh(v: number | string): string {
  const n = Number(v);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)} GWh`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(2)} MWh`;
  return `${n.toFixed(0)} kWh`;
}

function fmtKwhAxis(v: number): string {
  if (v === 0) return '0';
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} GWh`;
  if (v >= 1_000)     return `${(v / 1_000).toFixed(0)} MWh`;
  return `${v} kWh`;
}

function pad2(n: number) { return String(n).padStart(2, '0'); }

// ── Inline single-cell estimate editor (used in Year tab only) ────────────────

function EstimateCell({
  existing, plantId, periodType, year, month, weekNo, date, label, allowDelete,
}: {
  existing?: GenerationEstimate;
  plantId: number;
  periodType: string;
  year: number;
  month?: number;
  weekNo?: number;
  date?: string;
  label: string;
  allowDelete?: boolean;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [val, setVal]         = useState(existing ? String(existing.estimated_kwh) : '');
  const [notes, setNotes]     = useState(existing?.notes ?? '');

  const mut = useMutation({
    mutationFn: () => estimatesApi.upsert({
      plant_id: plantId, period_type: periodType, year,
      month: month ?? null, week_no: weekNo ?? null, date: date ?? null,
      estimated_kwh: Number(val), notes: notes || undefined,
    }),
    onSuccess: () => {
      toast.success(`${label} estimate saved`);
      qc.invalidateQueries({ queryKey: estimatesQueryKey(plantId, [year]) });
      setEditing(false);
    },
    onError: () => toast.error('Save failed'),
  });

  const delMut = useMutation({
    mutationFn: () => estimatesApi.delete(existing!.id),
    onSuccess: () => {
      toast.success(`${label} estimate removed`);
      qc.invalidateQueries({ queryKey: estimatesQueryKey(plantId, [year]) });
    },
    onError: () => toast.error('Delete failed'),
  });

  if (!editing) {
    return (
      <button
        onClick={() => { setVal(existing ? String(existing.estimated_kwh) : ''); setNotes(existing?.notes ?? ''); setEditing(true); }}
        className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
          existing
            ? 'border-brand-200 bg-brand-50/30 hover:border-brand-400'
            : 'border-dashed border-slate-300 hover:border-brand-300 hover:bg-slate-50'
        }`}
      >
        {existing ? (
          <div>
            <p className="text-sm font-semibold text-slate-900">{fmtKwh(existing.estimated_kwh)}</p>
            {existing.notes && <p className="text-xs text-slate-500 truncate">{existing.notes}</p>}
          </div>
        ) : (
          <p className="text-xs text-slate-400">+ Set estimate</p>
        )}
      </button>
    );
  }

  return (
    <div className="border border-brand-400 rounded-lg p-2 bg-brand-50/40 space-y-2">
      <input type="number" autoFocus className="input w-full text-sm py-1.5" value={val}
        onChange={e => setVal(e.target.value)} placeholder="kWh" />
      <input className="input w-full text-xs py-1.5" value={notes}
        onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)" />
      <div className="flex gap-1.5">
        <button onClick={() => mut.mutate()} disabled={mut.isPending || !val}
          className="btn-primary text-xs py-1 px-3 flex-1">
          {mut.isPending ? '…' : 'Save'}
        </button>
        {allowDelete && existing && (
          <button onClick={() => delMut.mutate()} disabled={delMut.isPending}
            className="btn-ghost text-xs py-1 px-2 text-red-500 hover:bg-red-50">
            {delMut.isPending ? '…' : '🗑'}
          </button>
        )}
        <button onClick={() => setEditing(false)} className="btn-ghost text-xs py-1 px-2">✕</button>
      </div>
    </div>
  );
}

// ── Year overview tab ─────────────────────────────────────────────────────────

function YearTab({ plantId, year, estimates, summaries }: {
  plantId: number; year: number;
  estimates: GenerationEstimate[];
  summaries: DailyGenerationSummary[];
}) {
  const monthEsts = estimates.filter(e => e.period_type === 'month');

  // Always derive from the live monthly estimates in the current query result.
  // This is immediately correct after any EstimateCell save + invalidation,
  // with no dependency on the DB year record being up-to-date.
  const annualKwh = monthEsts.reduce((s, e) => s + Number(e.estimated_kwh), 0);

  const actualByMonth = useMemo(() => {
    const m: Record<number, number> = {};
    summaries.forEach(s => {
      const mo = adStrToBS(s.date).getMonth() + 1; // BS month 1-indexed
      m[mo] = (m[mo] ?? 0) + Number(s.total_generation_kwh);
    });
    return m;
  }, [summaries]);

  const chartData = MONTHS.map((name, i) => {
    const mo  = i + 1;
    const est = monthEsts.find(e => e.month === mo);
    return {
      name,
      Estimated: est ? Math.round(est.estimated_kwh) : null,
      Actual:    Math.round(actualByMonth[mo] ?? 0) || null,
    };
  });

  const totalEst    = monthEsts.reduce((s, e) => s + e.estimated_kwh, 0);
  const totalActual = summaries.reduce((s, r) => s + Number(r.total_generation_kwh), 0);

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Annual Target ({year})</p>
          <span className="text-xs text-slate-400 italic">Auto-calculated from monthly estimates</span>
        </div>
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 rounded-lg bg-brand-50 border border-brand-200/60 px-4 py-3 text-center min-w-[140px]">
            <p className="text-xs text-slate-500 mb-1">Annual Target</p>
            <p className="text-xl font-bold text-brand-700">
              {annualKwh > 0 ? fmtKwh(annualKwh) : '—'}
            </p>
          </div>
          {totalActual > 0 && (
            <div className="flex-1 rounded-lg bg-emerald-50 border border-emerald-200/60 px-4 py-3 text-center min-w-[140px]">
              <p className="text-xs text-slate-500 mb-1">Actual YTD</p>
              <p className="text-xl font-bold text-emerald-700">{fmtKwh(totalActual)}</p>
            </div>
          )}
          {annualKwh > 0 && totalActual > 0 && (
            <div className={`flex-1 rounded-lg border px-4 py-3 text-center min-w-[140px] ${
              totalActual >= annualKwh ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'
            }`}>
              <p className="text-xs text-slate-500 mb-1">Achievement</p>
              <p className={`text-xl font-bold ${totalActual >= annualKwh ? 'text-emerald-700' : 'text-amber-700'}`}>
                {((totalActual / annualKwh) * 100).toFixed(1)}%
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Monthly Targets</p>
          {totalEst > 0 && <span className="text-xs text-slate-400">Total estimated: {fmtKwh(totalEst)}</span>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {MONTHS.map((name, i) => {
            const mo     = i + 1;
            const est    = monthEsts.find(e => e.month === mo);
            const actual = actualByMonth[mo] ?? 0;
            return (
              <div key={mo} className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-700">{name}</span>
                  {actual > 0 && <span className="text-xs text-emerald-600 font-mono">{fmtKwh(actual)}</span>}
                </div>
                <EstimateCell existing={est} plantId={plantId} periodType="month" year={year} month={mo} label={name} allowDelete />
              </div>
            );
          })}
        </div>
      </div>

      {(chartData.some(d => d.Estimated || d.Actual)) && (
        <div className="card">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">Estimated vs Actual — Monthly</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => fmtKwhAxis(v)} width={70} />
                <Tooltip formatter={(v: number) => [fmtKwh(v)]} />
                <Legend />
                <Bar dataKey="Estimated" fill="#6366f1" radius={[3,3,0,0]} />
                <Bar dataKey="Actual"    fill="#10b981" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Month plan tab — monthly target → auto-distribute → editable week/day table ──

function MonthPlanTab({ plantId, year, month, estimates, summaries }: {
  plantId: number; year: number; month: number;
  estimates: GenerationEstimate[];
  summaries: DailyGenerationSummary[];
}) {
  const qc = useQueryClient();

  // month is a BS month (1-indexed), year is a BS year
  const daysInMonth = bsDaysInMonth(year, month - 1);

  const days = useMemo(() => (
    Array.from({ length: daysInMonth }, (_, i) => {
      const bsDay = i + 1;
      const adDate = bsToAD(year, month - 1, bsDay); // convert BS → AD for API
      const dateStr = `${adDate.getFullYear()}-${pad2(adDate.getMonth() + 1)}-${pad2(adDate.getDate())}`;
      return {
        dateStr,
        bsLabel: `${bsDay} ${MONTHS[month - 1]} ${year}`,
        dayName: adDate.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNum:  bsDay,
        wk:      isoWeek(adDate),
      };
    })
  ), [year, month, daysInMonth]);

  const weekGroups = useMemo(() => {
    const g: Record<number, typeof days> = {};
    days.forEach(d => { (g[d.wk] ??= []).push(d); });
    return g;
  }, [days]);

  // ── form state ──────────────────────────────────────────────────────────────
  const [monthVal,    setMonthVal]    = useState('');
  const [dayVals,     setDayVals]     = useState<Record<string, string>>({});
  const [hourVals,    setHourVals]    = useState<Record<string, Record<number, string>>>({});
  const [expandedDay, setExpandedDay] = useState<string | null>(null);
  const [dirty,       setDirty]       = useState(false);

  // Re-sync form values from the server on every estimates refetch (including the
  // one triggered by our own Save All), so the fields always reflect saved truth.
  // Deliberately does NOT touch expandedDay — collapsing the panel the user just
  // saved makes it look like the data disappeared, even though it persisted fine.
  useEffect(() => {
    const mEst = estimates.find(e => e.period_type === 'month' && e.month === month && e.year === year);
    setMonthVal(mEst ? String(mEst.estimated_kwh) : '');

    const dv: Record<string, string> = {};
    estimates
      .filter(e => e.period_type === 'day' && e.month === month && e.year === year)
      .forEach(e => { if (e.date) dv[e.date.slice(0, 10)] = String(e.estimated_kwh); });
    setDayVals(dv);

    const hv: Record<string, Record<number, string>> = {};
    estimates
      .filter(e => e.period_type === 'hour' && e.month === month && e.year === year)
      .forEach(e => {
        if (e.date != null && e.hour != null) {
          const d = e.date.slice(0, 10);
          (hv[d] ??= {})[e.hour] = String(e.estimated_kwh);
        }
      });
    setHourVals(hv);
    setDirty(false);
  }, [estimates, month, year]);

  // Collapse any open hourly panel only when the user actually navigates to a
  // different month/year — not on every background/post-save refetch above.
  useEffect(() => {
    setExpandedDay(null);
  }, [month, year]);

  // Week totals derived from day inputs
  const weekTotals = useMemo(() => {
    const wt: Record<number, number> = {};
    days.forEach(d => { wt[d.wk] = (wt[d.wk] ?? 0) + (Number(dayVals[d.dateStr]) || 0); });
    return wt;
  }, [days, dayVals]);

  const monthTotal = useMemo(
    () => days.reduce((s, d) => s + (Number(dayVals[d.dateStr]) || 0), 0),
    [days, dayVals]
  );

  const actualByDay: Record<string, number> = useMemo(() => {
    const m: Record<string, number> = {};
    summaries
      .filter(s => {
        const nd = adStrToBS(s.date);
        return nd.getYear() === year && nd.getMonth() + 1 === month; // BS year + month
      })
      .forEach(s => { m[s.date.slice(0, 10)] = Number(s.total_generation_kwh); });
    return m;
  }, [summaries, month, year]);

  // ── actions ─────────────────────────────────────────────────────────────────
  function distribute(val?: string) {
    const monthly = Number(val ?? monthVal);
    if (!monthly || isNaN(monthly) || monthly <= 0) return;
    const daily = monthly / daysInMonth;
    const newDayVals: Record<string, string> = {};
    days.forEach(d => { newDayVals[d.dateStr] = daily.toFixed(2); });
    setDayVals(newDayVals);

    // Cascade the new day value down into any day that already has an hourly
    // breakdown open, so hours stay in sync instead of going stale against
    // the freshly redistributed day totals.
    setHourVals(prev => {
      const next: Record<string, Record<number, string>> = {};
      const per = (daily / 24).toFixed(2);
      Object.keys(prev).forEach(dateStr => {
        if (prev[dateStr] && Object.keys(prev[dateStr]).length > 0) {
          const hrs: Record<number, string> = {};
          for (let h = 0; h < 24; h++) hrs[h] = per;
          next[dateStr] = hrs;
        }
      });
      return next;
    });

    setDirty(true);
  }

  // Auto-distribute as soon as user finishes typing the monthly target
  function handleMonthBlur() {
    distribute();
  }

  function hourDistribute(dateStr: string) {
    const total = Number(dayVals[dateStr]);
    if (!total || isNaN(total) || total <= 0) return;
    const per = total / 24;
    const hrs: Record<number, string> = {};
    for (let h = 0; h < 24; h++) hrs[h] = per.toFixed(2);
    setHourVals(prev => ({ ...prev, [dateStr]: hrs }));
    setDirty(true);
  }

  // Bulk top-down fill: give every day in the month an even hourly split of
  // its current day target, without needing to expand each day individually.
  // Days with no day target yet (0 or blank) are left untouched.
  function fillAllHours() {
    setHourVals(prev => {
      const next = { ...prev };
      days.forEach(d => {
        const total = Number(dayVals[d.dateStr]);
        if (total > 0) {
          const per = (total / 24).toFixed(2);
          const hrs: Record<number, string> = {};
          for (let h = 0; h < 24; h++) hrs[h] = per;
          next[d.dateStr] = hrs;
        }
      });
      return next;
    });
    setDirty(true);
  }

  function toggleExpand(dateStr: string) {
    const willOpen = expandedDay !== dateStr;
    setExpandedDay(willOpen ? dateStr : null);
    const hasHours = hourVals[dateStr] && Object.keys(hourVals[dateStr]).length > 0;
    if (willOpen && !hasHours) hourDistribute(dateStr);
  }

  // Bottom-up: editing an hour recalculates that day's total from the sum of its
  // 24 hours. Week/month totals then update automatically since they're already
  // derived from dayVals via useMemo (weekTotals, monthTotal).
  function updateHourVal(dateStr: string, hour: number, value: string) {
    const newHrs = { ...(hourVals[dateStr] ?? {}), [hour]: value };
    setHourVals(prev => ({ ...prev, [dateStr]: newHrs }));
    const daySum = Object.values(newHrs).reduce((s, v) => s + (Number(v) || 0), 0);
    setDayVals(prev => ({ ...prev, [dateStr]: daySum.toFixed(2) }));
    setDirty(true);
  }

  const saveMut = useMutation({
    mutationFn: async () => {
      const monthly = Number(monthVal);
      const rows: Parameters<typeof estimatesApi.bulkUpsert>[0] = [];

      if (monthly > 0) {
        rows.push({
          plant_id: plantId, period_type: 'month', year, month,
          week_no: null, date: null, estimated_kwh: monthly,
        });
      }

      Object.entries(weekTotals).forEach(([wkStr, total]) => {
        if (total > 0) {
          rows.push({
            plant_id: plantId, period_type: 'week', year, month,
            week_no: Number(wkStr), date: null, estimated_kwh: Math.round(total),
          });
        }
      });

      days.forEach(d => {
        const val = Number(dayVals[d.dateStr]);
        if (val > 0) {
          rows.push({
            plant_id: plantId, period_type: 'day', year, month,
            week_no: null, date: d.dateStr, estimated_kwh: val,
          });
        }

        const hrs = hourVals[d.dateStr];
        if (hrs) {
          Object.entries(hrs).forEach(([hStr, hVal]) => {
            const v = Number(hVal);
            if (v > 0) {
              rows.push({
                plant_id: plantId, period_type: 'hour', year, month,
                week_no: null, date: d.dateStr, hour: Number(hStr), estimated_kwh: v,
              });
            }
          });
        }
      });

      if (rows.length > 0) await estimatesApi.bulkUpsert(rows);
    },
    onSuccess: () => {
      toast.success('Planning saved');
      qc.invalidateQueries({ queryKey: estimatesQueryKey(plantId, [year]) });
      setDirty(false);
    },
    onError: () => toast.error('Save failed'),
  });

  // ── render ──────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Monthly target + actions */}
      <div className="card">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
          Monthly Target — {MONTHS[month - 1]} {year}
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[160px] max-w-xs">
            <label className="block text-xs text-slate-500 mb-1.5">Target (kWh)</label>
            <input
              type="number"
              min="0"
              className="input w-full"
              value={monthVal}
              onChange={e => { setMonthVal(e.target.value); setDirty(true); }}
              onBlur={handleMonthBlur}
              placeholder="e.g. 500000"
            />
            <p className="text-xs text-slate-400 mt-1">Weeks &amp; days auto-fill on input</p>
          </div>
          <button
            onClick={() => distribute()}
            disabled={!monthVal || Number(monthVal) <= 0}
            className="btn-ghost text-sm border border-brand-300 text-brand-600 hover:bg-brand-50"
          >
            Re-distribute
          </button>
          <button
            onClick={fillAllHours}
            disabled={monthTotal <= 0}
            title="Give every day an even hourly split of its current target"
            className="btn-ghost text-sm border border-brand-300 text-brand-600 hover:bg-brand-50"
          >
            Fill Hourly (All Days)
          </button>
          <button
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending || !dirty}
            className="btn-primary min-w-[100px]"
          >
            {saveMut.isPending ? 'Saving…' : 'Save All'}
          </button>
        </div>

        {/* Summary row */}
        {(monthTotal > 0 || Number(monthVal) > 0) && (
          <div className="mt-4 flex flex-wrap gap-3">
            {Number(monthVal) > 0 && (
              <div className="rounded-lg bg-brand-50 border border-brand-200/60 px-4 py-2 text-center">
                <p className="text-xs text-slate-500">Monthly Target</p>
                <p className="text-base font-bold text-brand-700">{fmtKwh(Number(monthVal))}</p>
              </div>
            )}
            {monthTotal > 0 && (
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-2 text-center">
                <p className="text-xs text-slate-500">Sum of Daily Targets</p>
                <p className={`text-base font-bold ${Math.abs(monthTotal - Number(monthVal)) < 1 ? 'text-emerald-700' : 'text-amber-700'}`}>
                  {fmtKwh(monthTotal)}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Week / day breakdown table */}
      {Object.entries(weekGroups).map(([wkStr, wkDays]) => {
        const wk         = Number(wkStr);
        const weekTarget = weekTotals[wk] ?? 0;
        const weekActual = wkDays.reduce((s, d) => s + (actualByDay[d.dateStr] ?? 0), 0);

        return (
          <div key={wk} className="card p-0 overflow-hidden">
            {/* Week header */}
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-hydro-border">
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Week {wk}</span>
              <div className="flex items-center gap-4 text-xs">
                {weekTarget > 0 && (
                  <span className="text-brand-700 font-mono font-medium">
                    Target: {fmtKwh(weekTarget)}
                  </span>
                )}
                {weekActual > 0 && (
                  <span className="text-emerald-600 font-mono font-medium">
                    Actual: {fmtKwh(weekActual)}
                  </span>
                )}
                {weekTarget > 0 && weekActual > 0 && (
                  <span className={`px-2 py-0.5 rounded-full font-medium ${
                    weekActual >= weekTarget ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {((weekActual / weekTarget) * 100).toFixed(1)}%
                  </span>
                )}
              </div>
            </div>

            {/* Day rows */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-hydro-border text-xs text-slate-500 uppercase tracking-wider">
                    <th className="px-4 py-2.5 text-left w-24">Day</th>
                    <th className="px-4 py-2.5 text-left w-28">Date (AD / BS)</th>
                    <th className="px-4 py-2.5 text-left w-44">Target (kWh)</th>
                    <th className="px-4 py-2.5 text-left">Actual</th>
                    <th className="px-4 py-2.5 text-left">Achievement</th>
                  </tr>
                </thead>
                <tbody>
                  {wkDays.map(d => {
                    const actual      = actualByDay[d.dateStr] ?? 0;
                    const target      = Number(dayVals[d.dateStr]) || 0;
                    const achievement = target > 0 && actual > 0
                      ? ((actual / target) * 100).toFixed(1)
                      : null;

                    return (
                      <React.Fragment key={d.dateStr}>
                      <tr className="table-row border-b border-hydro-border last:border-0">
                        <td className="px-4 py-2.5 text-xs font-semibold text-slate-700">
                          <button
                            onClick={() => toggleExpand(d.dateStr)}
                            className="mr-1.5 text-slate-400 hover:text-brand-600"
                            title="Hourly breakdown"
                          >
                            {expandedDay === d.dateStr ? '▾' : '▸'}
                          </button>
                          {d.dayName} {d.dayNum}
                        </td>
                        <td className="px-4 py-2.5 text-xs">
                          <div className="text-slate-500 font-mono">{d.dateStr}</div>
                          <div className="text-brand-600 font-medium mt-0.5">{d.bsLabel}</div>
                        </td>
                        <td className="px-4 py-2.5">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="input text-sm py-1 w-40"
                            value={dayVals[d.dateStr] ?? ''}
                            onChange={e => {
                              setDayVals(prev => ({ ...prev, [d.dateStr]: e.target.value }));
                              setDirty(true);
                            }}
                            placeholder="0"
                          />
                        </td>
                        <td className="px-4 py-2.5 text-xs font-mono text-emerald-600">
                          {actual > 0 ? fmtKwh(actual) : '—'}
                        </td>
                        <td className="px-4 py-2.5">
                          {achievement && (
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              Number(achievement) >= 100
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-amber-100 text-amber-700'
                            }`}>
                              {achievement}%
                            </span>
                          )}
                        </td>
                      </tr>
                      {expandedDay === d.dateStr && (
                        <tr className="bg-brand-50/30 border-b border-hydro-border">
                          <td colSpan={5} className="px-4 py-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-semibold text-slate-600">
                                Hourly breakdown — {d.dateStr} <span className="text-brand-600">({d.bsLabel})</span>
                              </span>
                              <button
                                onClick={() => hourDistribute(d.dateStr)}
                                className="btn-ghost text-xs py-0.5 px-2 border border-brand-300 text-brand-600"
                              >
                                Re-distribute evenly
                              </button>
                            </div>
                            <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
                              {Array.from({ length: 24 }, (_, h) => (
                                <div key={h}>
                                  <label className="block text-[10px] text-slate-400 mb-0.5">{pad2(h)}:00</label>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    className="input text-xs py-1 w-full"
                                    value={hourVals[d.dateStr]?.[h] ?? ''}
                                    onChange={e => updateHourVal(d.dateStr, h, e.target.value)}
                                    placeholder="0"
                                  />
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                  {/* Week subtotal row */}
                  <tr className="bg-slate-50 border-t-2 border-hydro-border">
                    <td colSpan={2} className="px-4 py-2 text-xs font-bold text-slate-600 uppercase tracking-wide">
                      Week {wk} Total
                    </td>
                    <td className="px-4 py-2 text-xs font-bold text-brand-700 font-mono">
                      {weekTarget > 0 ? fmtKwh(weekTarget) : '—'}
                    </td>
                    <td className="px-4 py-2 text-xs font-bold text-emerald-600 font-mono">
                      {weekActual > 0 ? fmtKwh(weekActual) : '—'}
                    </td>
                    <td className="px-4 py-2" />
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* Sticky save bar when there are unsaved changes */}
      {dirty && (
        <div className="sticky bottom-4 flex justify-end pointer-events-none">
          <button
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending}
            className="btn-primary shadow-xl px-8 pointer-events-auto"
          >
            {saveMut.isPending ? 'Saving…' : 'Save All Changes'}
          </button>
        </div>
      )}

    </div>
  );
}

// ── Main Planning page ────────────────────────────────────────────────────────

export default function Planning() {
  const [year,  setYear]  = useState(CURRENT_YEAR);      // BS year
  const [month, setMonth] = useState(getCurrentBSMonth() + 1); // BS month 1-indexed
  const [tab,   setTab]   = useState<PlanTab>('year');
  const { user } = useAuthStore();
  const plantId = user?.plant_id ?? 1;

  const { data: estimates = [] } = useEstimates(plantId, [year]);

  const { data: summaryData } = useQuery({
    queryKey: ['shift-log-daily-totals', year],
    queryFn:  () => {
      const { from, to } = bsYearRangeAD(year);
      return shiftLogApi.dailyTotals({ from, to }).then(r => r.data);
    },
  });

  const summaries = summaryData?.data ?? [];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Generation Planning</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Set a monthly target, auto-distribute to weeks &amp; days, then save all at once.
        </p>
      </div>

      {/* Controls */}
      <div className="card flex flex-wrap gap-4 items-center">
        {/* Year picker (BS) */}
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500">Year (BS)</label>
          <div className="flex items-center gap-1">
            <button onClick={() => setYear(y => y - 1)} className="btn-ghost text-xs py-1 px-2">‹</button>
            <select value={year} onChange={e => setYear(Number(e.target.value))} className="input text-sm py-1 w-20 text-center font-bold">
              {BS_YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button onClick={() => setYear(y => y + 1)} className="btn-ghost text-xs py-1 px-2">›</button>
          </div>
        </div>

        {/* Month picker — only on monthly plan tab */}
        {tab === 'month' && (
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500">Month</label>
            <select
              value={month}
              onChange={e => setMonth(Number(e.target.value))}
              className="input text-sm py-1.5 w-32"
            >
              {BS_MONTHS_FULL.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
          </div>
        )}

        {/* Tab switcher */}
        <div className="ml-auto flex gap-1 border border-hydro-border rounded-lg overflow-hidden">
          {(['year', 'month'] as PlanTab[]).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-1.5 text-xs font-medium capitalize transition-colors ${
                tab === t ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {t === 'year' ? 'Year Overview' : 'Month Plan'}
            </button>
          ))}
        </div>
      </div>

      {tab === 'year' && (
        <YearTab plantId={plantId} year={year} estimates={estimates} summaries={summaries} />
      )}
      {tab === 'month' && (
        <MonthPlanTab
          plantId={plantId} year={year} month={month}
          estimates={estimates} summaries={summaries}
        />
      )}
    </div>
  );
}
